/**
 * Hand-written wire types for the report endpoints.
 *
 * <p>These exist because `attachmentImagePublicIds` was added to the backend's
 * `CreateContentReportRequest` and `ReportUserRequest` and the generated client cannot be regenerated
 * without a running local backend (AGENTS.md rule 6). The `ClientFactory` wrappers that consume them
 * are the sanctioned shape; when the generated client is next refreshed these can be retired in favour
 * of the typed client methods.
 *
 * <p><b>There is no compile-time link between these shapes and the server.</b> Field names must match
 * the Java DTOs' setter-derived property names exactly: `reason`, `reasonType`,
 * `attachmentImagePublicIds`, `username`/`targetUsername`, `block`.
 */

/** Mirrors `ReportAttachmentService.MAX_ATTACHMENTS_PER_REPORT`. A hint here; the server is the limit. */
export const MAX_REPORT_ATTACHMENTS = 3;

/** Mirrors the backend `ReportFamily` enum: which report table an id belongs to. */
export type ReportFamily = "CONTENT" | "USER";

/** Body of every content-report POST (discussion, thought, group, meme template, meme). */
export type ContentReportBody = {
    reason: string;
    reasonType: string;
    /** Omitted entirely when empty, so a server that predates attachments sees an unchanged payload. */
    attachmentImagePublicIds?: string[];
};

/** Body of the account-report POST. */
export type ReportUserBody = {
    username: string;
    targetUsername: string;
    reason: string;
    reasonType: string;
    block: boolean;
    attachmentImagePublicIds?: string[];
};

/**
 * Body of the direct-message report POST.
 *
 * <p>Deliberately NOT `ContentReportBody & { block }`: a DM report offers no screenshot picker (the
 * evidence is the conversation, captured verbatim by the server), and a type that carried
 * `attachmentImagePublicIds` would invite a caller to send it to an endpoint whose DTO has no such
 * field. Mirrors `ReportChatMessageRequest` — `reason`, `reasonType`, `block`.
 */
export type ChatMessageReportBody = {
    reason: string;
    reasonType: string;
    block: boolean;
};

/**
 * Mirrors the backend `ReportMessageSnapshotDTO`: one captured message in a reported conversation.
 *
 * <p>`body` is empty for a message the reporter could not see. `deletedAtCapture` must be RENDERED, not
 * used to filter the row out: "this was deleted" is signal a moderator needs, and hiding it is the same
 * divergence `SupportQueueRepository.findMessagesForSupportThread` still carries on the staff side.
 */
export type ReportMessageSnapshot = {
    position: number;
    reportedMessage: boolean;
    messagePublicId: string;
    senderUsername: string;
    body: string;
    sentAt: string;
    deletedAtCapture: boolean;
};

/** Mirrors the backend `ReportAttachmentDTO`. Read by platform staff only. */
export type ReportAttachment = {
    imagePublicId: string;
    name?: string | null;
    url: string;
    thumbnailUrl: string;
    uploadedAt?: string | null;
};
